import { IReducedAnswerState } from '@/modules/SurveyForm';
import {
  Button,
  HStack,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiInfo, FiStar } from 'react-icons/fi';

interface FavoriteButtonProps {
  isFavorited: boolean;
  onSaveFavorite: (name: string, values: IReducedAnswerState) => void;
  onRemoveFavorite: () => void;
  getCurrentValues: () => IReducedAnswerState;
  onCheckFavorite?: () => boolean; // Returns true if favorited
}

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  isFavorited,
  onSaveFavorite,
  onRemoveFavorite,
  getCurrentValues,
  onCheckFavorite,
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [favoriteName, setFavoriteName] = useState('');
  const [localIsFavorited, setLocalIsFavorited] = useState(isFavorited);

  // Sync with prop
  React.useEffect(() => {
    setLocalIsFavorited(isFavorited);
  }, [isFavorited]);

  const handleSaveFavorite = useCallback(() => {
    if (!favoriteName.trim()) return;

    const values = getCurrentValues();
    onSaveFavorite(favoriteName.trim(), values);
    setFavoriteName('');
    onClose();
  }, [favoriteName, getCurrentValues, onSaveFavorite, onClose]);

  const handleMouseEnter = useCallback(() => {
    // Check favorite status when hovering
    const result = onCheckFavorite?.();
    if (result !== undefined) {
      setLocalIsFavorited(result);
    }
  }, [onCheckFavorite]);

  const handleStarClick = useCallback(() => {
    // Check if current values match a favorite and get the result
    const currentlyFavorited = onCheckFavorite?.() ?? localIsFavorited;
    setLocalIsFavorited(currentlyFavorited);
    
    if (currentlyFavorited) {
      onRemoveFavorite();
      setLocalIsFavorited(false);
    } else {
      onOpen();
    }
  }, [localIsFavorited, onRemoveFavorite, onOpen, onCheckFavorite]);

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="top-end"
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <Tooltip
          label={
            localIsFavorited
              ? t('exporter.removeFavorite', 'Remover dos favoritos')
              : t('exporter.addFavorite', 'Salvar como favorito')
          }
        >
          <IconButton
            aria-label="Favoritar configuração"
            icon={
              <FiStar
                fill={localIsFavorited ? 'currentColor' : 'none'}
                size={20}
              />
            }
            variant="outline"
            colorScheme={localIsFavorited ? 'yellow' : 'gray'}
            size="md"
            onClick={handleStarClick}
            onMouseEnter={handleMouseEnter}
          />
        </Tooltip>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold">
          <HStack justify="space-between" align="center" pr={6}>
            <Text>{t('exporter.saveFavorite', 'Salvar como favorito')}</Text>
            <Tooltip
              label={t(
                'exporter.dateQuestionsNotSaved',
                'Perguntas de data não são salvas nos favoritos',
              )}
              placement="top"
              hasArrow
            >
              <IconButton
                aria-label="Informação"
                icon={<FiInfo size={16} />}
                variant="ghost"
                size="xs"
                colorScheme="gray"
              />
            </Tooltip>
          </HStack>
        </PopoverHeader>
        <PopoverBody>
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" color="gray.600">
              {t(
                'exporter.favoriteName',
                'Digite um nome para este favorito:',
              )}
            </Text>
            <Input
              placeholder={t('exporter.favoriteNamePlaceholder', 'Ex: Relatório mensal')}
              value={favoriteName}
              onChange={(e) => setFavoriteName(e.target.value)}
              size="sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveFavorite();
                }
              }}
              autoFocus
            />
            <HStack justify="flex-end" spacing={2}>
              <Button size="sm" variant="ghost" onClick={onClose}>
                {t('common.cancel', 'Cancelar')}
              </Button>
              <Button
                size="sm"
                colorScheme="primary"
                onClick={handleSaveFavorite}
                isDisabled={!favoriteName.trim()}
              >
                {t('common.save', 'Salvar')}
              </Button>
            </HStack>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default FavoriteButton;
